import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { personName } from '@/i18n/personName';
import { sharesApi, type SharedWithMeItem } from '@/api/shares';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorState } from '@/components/ErrorState';
import { useConfirm } from '@/components/useConfirm';
import { useMe } from '@/session/meHooks';
import type { SettingsNotify } from './settingsHref';

interface SharingTabProps {
  onNotify: SettingsNotify;
}

export function SharingTab({ onNotify }: SharingTabProps) {
  const { t } = useTranslation();
  const me = useMe();
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [loadError, setLoadError] = useState<unknown>(undefined);
  const [rows, setRows] = useState<SharedWithMeItem[]>([]);
  const leaving = useConfirm<SharedWithMeItem>();
  const started = useRef(false);

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      setRows(await sharesApi.sharedWithMe());
      setStatus('ready');
    } catch (error) {
      setLoadError(error);
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void load();
  }, [load]);

  async function handleLeave() {
    await leaving.run(
      async (row) => {
        await sharesApi.remove(row.virtualAccountId, row.shareId);
        onNotify(t('virtualAccount.left'));
        await load();
      },
      () => t('settings.errorGeneric'),
    );
  }

  if (status === 'loading') {
    return <Typography variant="body2">{t('common.loading')}</Typography>;
  }
  if (status === 'error') {
    return (
      <ErrorState
        error={loadError}
        what={t('errors.what.sharing')}
        onRetry={() => {
          started.current = false;
          void load();
        }}
      />
    );
  }

  // One group per owner, told apart by `ownerUserId` (TMMAPI-0142), never by name (0097 §E 35).
  const groups = new Map<number, SharedWithMeItem[]>();
  for (const row of rows) {
    const list = groups.get(row.ownerUserId) ?? [];
    list.push(row);
    groups.set(row.ownerUserId, list);
  }

  return (
    <Stack spacing={3} sx={{ maxWidth: 640 }}>
      {me.tier === 'GUEST' && <Typography variant="body1">{t('subscribe.guestNote')}</Typography>}
      {rows.length === 0 ? (
        <Typography variant="body2">{t('settings.sharingEmpty')}</Typography>
      ) : (
        [...groups.values()].map((items) => (
          <Box key={items[0]?.shareId}>
            <Typography variant="h3" gutterBottom>
              {personName(items[0]?.ownerDisplayName, t)}
            </Typography>
            <Stack spacing={1}>
              {items.map((item) => (
                <Stack
                  key={item.shareId}
                  direction="row"
                  spacing={2}
                  sx={{ alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <Typography variant="body1">{item.virtualAccountName}</Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => leaving.ask(item)}
                  >
                    {t('virtualAccount.leave')}
                  </Button>
                </Stack>
              ))}
            </Stack>
          </Box>
        ))
      )}
      {leaving.target !== null && (
        <ConfirmDialog
          title={t('virtualAccount.leaveTitle', { name: leaving.target.virtualAccountName })}
          consequence={t('virtualAccount.leaveConsequence')}
          confirmLabel={t('virtualAccount.leaveConfirm')}
          destructive
          busy={leaving.busy}
          error={leaving.error}
          onConfirm={() => void handleLeave()}
          onCancel={leaving.cancel}
        />
      )}
    </Stack>
  );
}
